import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const loadingMessages = [
  "Analyzing facial aesthetics...",
  "Calibrating smile line...",
  "Applying digital cosmetic enhancements...",
  "Refining tooth morphology & shade...",
  "Ensuring photorealistic results...",
  "Polishing the final simulation...",
  "Finalizing your smile design..."
];

const CustomLoader: React.FC = () => (
  <div className="relative w-48 h-48 flex items-center justify-center">
    {/* Outer spinner with glow */}
    <div
      className="absolute w-full h-full border-t-[6px] border-teal-500 rounded-full animate-spin-slow"
      style={{ boxShadow: '0 0 10px #14b8a6' }}
    />
    {/* Middle spinner, reverse direction */}
    <div
      className="absolute w-3/4 h-3/4 border-b-[10px] border-teal-500/70 rounded-full animate-spin-medium-reverse"
      style={{ boxShadow: '0 0 15px #14b8a6' }}
    />
    {/* Inner spinner, fast */}
    <div
      className="absolute w-1/2 h-1/2 border-t-[14px] border-teal-500/40 rounded-full animate-spin-fast"
      style={{ boxShadow: '0 0 20px #14b8a6' }}
    />
  </div>
);

interface LoaderProps {
  message?: string;
}

const Loader: React.FC<LoaderProps> = ({ message }) => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    // If a specific message is provided, don't cycle through the default messages.
    if (message) return;

    const interval = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [message]);

  const displayMessage = message || loadingMessages[messageIndex];

  return (
    <div
      className="absolute inset-0 bg-zinc-50/80 dark:bg-zinc-950/80 backdrop-blur-2xl flex flex-col items-center justify-center z-50 animate-fade-in"
      role="status"
      aria-live="polite"
    >
      <CustomLoader />
      <div className="text-center mt-8 w-full max-w-sm px-4">
        <div className="h-14 flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={displayMessage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="text-lg font-medium text-zinc-800 dark:text-zinc-100 leading-snug"
            >
              {displayMessage}
            </motion.p>
          </AnimatePresence>
        </div>
        <p className="mt-2 text-sm font-normal text-zinc-600 dark:text-zinc-400">
          This may take a moment. Please wait.
        </p>
      </div>
    </div>
  );
};

export default Loader;